import { Checker, ID, HEX, MODEL_ID, type Issue } from '../presentation/validate.ts';
import type { Board } from '../protocol/ui';
import { STANDEE_ID } from './three/standee/ids.ts';
import { BOARD_EFFECTS, DECAL_PRIMITIVES, DIE_SIDES, DIE_STYLE_FIELDS, PIECE_SHAPES, PIECE_STYLE_FIELDS, PRIMITIVE_STYLE_FIELDS, PRIMITIVE_TYPES, QUAD_ALPHA, QUAD_STYLE_FIELDS, SLAB_STYLE_FIELDS, STANDEE_STYLE_FIELDS, THREE_LAYER_MODES, type BoardVisuals, type DieStyle, type ObjectMatch, type PrimitiveStyle, type QuadStyle, type SlabStyle, type StandeeStyle, type ThreeRuntime } from './types.ts';
import type { BrickInstance, SceneFile } from '../presentation/types.ts';
import type { AtlasesFile } from '../faces/types.ts';
import { frameIndex } from '../faces/art.ts';

const OBJECT_MATCH_KINDS = ['card', 'token', 'worker', 'tile', 'space', 'node', 'edge', 'marker', 'die'];
/** An observed-object matcher: the same bounded shape for presets, terrain styles and piece styles. */
function checkMatch(c: Checker, match: unknown, q: string) {
  if (!c.object(match, q, [], ['kind', 'catalog', 'tags', 'fields'])) return;
  const m = match as ObjectMatch;
  if (m.kind !== undefined) c.oneOf(m.kind, q + '.kind', OBJECT_MATCH_KINDS);
  for (const key of ['catalog', 'tags'] as const) if (m[key] !== undefined && c.array(m[key], q + '.' + key, 1, 16)) m[key].forEach((v, k) => c.string(v, `${q}.${key}[${k}]`, 128, ID));
  if (m.fields !== undefined && c.object(m.fields, q + '.fields', [], m.fields && typeof m.fields === 'object' ? Object.keys(m.fields) : [])) {
    if (Object.keys(m.fields).length > 16) c.error(q, 'At most 16 observed field predicates.');
    for (const [key, value] of Object.entries(m.fields)) { c.string(key, q + '.fields', 128, ID); if (!(typeof value === 'boolean' || typeof value === 'number' && Number.isFinite(value) || typeof value === 'string' && value.length <= 256)) c.error(q, 'Field predicates must be bounded scalar values.'); }
  }
}
const PROFILE_ID = /^[a-z][a-z0-9-]{0,31}$/;
const STANDEE_PREFIX = /^[a-z0-9-]{1,24}$/;
const color = (c: Checker, value: unknown, path: string) => c.string(value, path, 7, HEX);
function checkStandee(c: Checker, value: unknown, q: string) {
  if (!c.object(value, q, [...STANDEE_STYLE_FIELDS.required], [...STANDEE_STYLE_FIELDS.optional])) return;
  const s = value as unknown as StandeeStyle;
  if (s.id !== undefined) c.string(s.id, q + '.id', 56, STANDEE_ID);
  if ((s.id === undefined) === (s.idFrom === undefined)) c.error(q, 'Use exactly one of id or idFrom.');
  if (s.idFrom !== undefined) c.oneOf(s.idFrom, q + '.idFrom', ['catalog', 'field']);
  if ((s.idFrom === 'field') !== (s.field !== undefined)) c.error(q, 'field is required with idFrom "field" and allowed only there.');
  if (s.field !== undefined) c.string(s.field, q + '.field', 128, ID);
  if (s.prefix !== undefined) {
    if (s.idFrom === undefined) c.error(q, 'prefix needs idFrom.');
    c.string(s.prefix, q + '.prefix', 24, STANDEE_PREFIX);
  }
  if (s.height !== undefined) c.number(s.height, q + '.height', 0.05, 8);
  if (s.mounting !== undefined) c.oneOf(s.mounting, q + '.mounting', ['upright', 'ground']);
  if (s.facing !== undefined) c.oneOf(s.facing, q + '.facing', ['fixed', 'camera']);
  if (s.edge !== undefined && s.edge !== 'art' && s.edge !== 'owner') color(c, s.edge, q + '.edge');
  if (s.mounting === 'ground' && s.facing === 'camera') c.error(q, 'facing camera needs an upright standee.');
}
function checkSlab(c: Checker, value: unknown, q: string) {
  if (!c.object(value, q, [...SLAB_STYLE_FIELDS.required], [...SLAB_STYLE_FIELDS.optional])) return;
  const s = value as unknown as SlabStyle;
  if (s.size !== undefined) c.number(s.size, q + '.size', 0.05, 8);
  if (s.aspect !== undefined) c.number(s.aspect, q + '.aspect', 0.2, 5);
  if (s.thickness !== undefined) c.number(s.thickness, q + '.thickness', 0.005, 0.5);
  if (s.radius !== undefined) c.number(s.radius, q + '.radius', 0, 0.5);
  if (s.color !== undefined && s.color !== 'owner') color(c, s.color, q + '.color');
}
const QUAD_ASSET = /^asset:[A-Za-z0-9][A-Za-z0-9:_./-]{0,127}$/;
const QUAD_PREFIX = /^[A-Za-z0-9][A-Za-z0-9:_./-]{0,63}$/;
function checkQuad(c: Checker, value: unknown, q: string) {
  if (!c.object(value, q, [...QUAD_STYLE_FIELDS.required], [...QUAD_STYLE_FIELDS.optional])) return;
  const s = value as unknown as QuadStyle;
  if (s.asset !== undefined) c.string(s.asset, q + '.asset', 134, QUAD_ASSET);
  if ((s.asset === undefined) === (s.assetFrom === undefined)) c.error(q, 'Use exactly one of asset or assetFrom.');
  if (s.assetFrom !== undefined) c.oneOf(s.assetFrom, q + '.assetFrom', ['catalog', 'field']);
  if ((s.assetFrom === 'field') !== (s.field !== undefined)) c.error(q, 'field is required with assetFrom "field" and allowed only there.');
  if (s.field !== undefined) c.string(s.field, q + '.field', 128, ID);
  if (s.prefix !== undefined) {
    if (s.assetFrom === undefined) c.error(q, 'prefix needs assetFrom.');
    c.string(s.prefix, q + '.prefix', 64, QUAD_PREFIX);
  }
  if (s.width !== undefined && s.height !== undefined) c.error(q, 'Use at most one of width and height.');
  if (s.width !== undefined) c.number(s.width, q + '.width', 0.05, 8);
  if (s.height !== undefined) c.number(s.height, q + '.height', 0.05, 8);
  if (s.mounting !== undefined) c.oneOf(s.mounting, q + '.mounting', ['upright', 'ground']);
  if (s.facing !== undefined) c.oneOf(s.facing, q + '.facing', ['fixed', 'camera']);
  if (s.alpha !== undefined) c.oneOf(s.alpha, q + '.alpha', QUAD_ALPHA);
  if (s.opacity !== undefined) c.number(s.opacity, q + '.opacity', 0.05, 1);
  if (s.alphaThreshold !== undefined) {
    c.number(s.alphaThreshold, q + '.alphaThreshold', 1, 254, true);
    if (s.alpha === 'blend') c.error(q, 'alphaThreshold needs alpha "cutout".');
  }
  if (s.mounting === 'ground' && s.facing === 'camera') c.error(q, 'facing camera needs an upright quad.');
  if (s.back !== undefined && s.back !== 'mirror' && s.back !== 'none') color(c, s.back, q + '.back');
  if (s.back !== undefined && s.mounting === 'ground') c.error(q, 'back needs an upright quad.');
}
const DECAL = /^(asset:[A-Za-z0-9][A-Za-z0-9:_./-]{0,127}|icon:[A-Za-z0-9][A-Za-z0-9:_.-]{0,127})$/;
function checkPrimitive(c: Checker, value: unknown, q: string) {
  if (!c.object(value, q, [...PRIMITIVE_STYLE_FIELDS.required], [...PRIMITIVE_STYLE_FIELDS.optional])) return;
  const s = value as unknown as PrimitiveStyle;
  c.oneOf(s.type, q + '.type', PRIMITIVE_TYPES);
  if (s.size !== undefined) c.number(s.size, q + '.size', 0.05, 4);
  if (s.aspect !== undefined) c.number(s.aspect, q + '.aspect', 0.25, 4);
  if (s.decal !== undefined && c.string(s.decal, q + '.decal', 140, DECAL) && !(DECAL_PRIMITIVES as readonly string[]).includes(s.type)) c.error(q, 'decal needs a flat top: coin, disc, cylinder, hex-prism or cube.');
  if (s.decalScale !== undefined) c.number(s.decalScale, q + '.decalScale', 0.2, 1);
  if (s.decalInk !== undefined) color(c, s.decalInk, q + '.decalInk');
}
function checkDie(c: Checker, value: unknown, q: string) {
  if (!c.object(value, q, [...DIE_STYLE_FIELDS.required], [...DIE_STYLE_FIELDS.optional])) return;
  const s = value as unknown as DieStyle;
  if (!(DIE_SIDES as readonly unknown[]).includes(s.sides)) c.error(q + '.sides', 'Must be one of 4, 6, 8, 10, 12, 20.');
  if (s.valueField !== undefined) c.string(s.valueField, q + '.valueField', 128, ID);
  if (s.size !== undefined) c.number(s.size, q + '.size', 0.05, 4);
  if (s.ink !== undefined) color(c, s.ink, q + '.ink');
  if (s.pips !== undefined && typeof s.pips !== 'boolean') c.error(q + '.pips', 'Must be a boolean.');
  if (s.roll !== undefined && typeof s.roll !== 'boolean') c.error(q + '.roll', 'Must be a boolean.');
  if (s.pips === true && s.sides !== 6) c.error(q, 'pips are available on d6 only.');
}
/** Structural checks for the three-board runtime block; capability and model references are checked separately. */
function checkThree(c: Checker, three: unknown, p: string) {
  if (!c.object(three, p, ['cameraProfiles', 'defaultProfile'], ['terrain', 'pieces', 'water', 'numberTokens', 'ambient', 'layers'])) return;
  const t = three as unknown as ThreeRuntime, ids = new Set<string>();
  if (c.array(t.cameraProfiles, p + '.cameraProfiles', 1, 4)) t.cameraProfiles.forEach((profile, i) => {
    const q = `${p}.cameraProfiles[${i}]`;
    if (!c.object(profile, q, ['id', 'label', 'pitchDeg', 'distance', 'yaw'], ['projection', 'fovDeg', 'transitionMs'])) return;
    if (c.string(profile.id, q + '.id', 32, PROFILE_ID)) { if (ids.has(profile.id)) c.error(q + '.id', 'Duplicate camera profile ID.'); ids.add(profile.id); }
    c.string(profile.label, q + '.label', 32);
    c.number(profile.pitchDeg, q + '.pitchDeg', 10, 90); c.number(profile.distance, q + '.distance', 50, 20000);
    if (profile.yaw !== 'keep') c.number(profile.yaw, q + '.yaw', 0, 359);
    if (profile.projection !== undefined) c.oneOf(profile.projection, q + '.projection', ['perspective', 'orthographic']);
    if (profile.fovDeg !== undefined) c.number(profile.fovDeg, q + '.fovDeg', 20, 80);
    if (profile.transitionMs !== undefined) c.number(profile.transitionMs, q + '.transitionMs', 0, 2000);
  });
  if (c.string(t.defaultProfile, p + '.defaultProfile', 32, PROFILE_ID) && !ids.has(t.defaultProfile)) c.error(p + '.defaultProfile', 'Must name one of the camera profiles.');
  if (t.terrain !== undefined && c.array(t.terrain, p + '.terrain', 0, 32)) t.terrain.forEach((style, i) => {
    const q = `${p}.terrain[${i}]`;
    if (!c.object(style, q, ['match', 'color', 'height'], ['top', 'emissive'])) return;
    checkMatch(c, style.match, q + '.match'); color(c, style.color, q + '.color'); c.number(style.height, q + '.height', 0, 200);
    if (style.top !== undefined) color(c, style.top, q + '.top');
    if (style.emissive !== undefined) color(c, style.emissive, q + '.emissive');
  });
  if (t.pieces !== undefined && c.array(t.pieces, p + '.pieces', 0, 64)) t.pieces.forEach((style, i) => {
    const q = `${p}.pieces[${i}]`;
    if (!c.object(style, q, [...PIECE_STYLE_FIELDS.required], [...PIECE_STYLE_FIELDS.optional])) return;
    checkMatch(c, style.match, q + '.match');
    if ([style.model, style.standee, style.slab, style.primitive, style.die, style.quad].filter(v => v !== undefined).length > 1) c.error(q, 'Use at most one of model, standee, slab, primitive, die or quad per piece style.');
    if (style.standee !== undefined) checkStandee(c, style.standee, q + '.standee');
    if (style.slab !== undefined) checkSlab(c, style.slab, q + '.slab');
    if (style.primitive !== undefined) checkPrimitive(c, style.primitive, q + '.primitive');
    if (style.die !== undefined) checkDie(c, style.die, q + '.die');
    if (style.quad !== undefined) checkQuad(c, style.quad, q + '.quad');
    if (style.model !== undefined) c.string(style.model, q + '.model', 64, MODEL_ID);
    if (style.shape !== undefined) c.oneOf(style.shape, q + '.shape', PIECE_SHAPES);
    if (style.color !== undefined && style.color !== 'owner') color(c, style.color, q + '.color');
    if (style.scale !== undefined) c.number(style.scale, q + '.scale', 0.05, 20);
    if (style.yOffset !== undefined) c.number(style.yOffset, q + '.yOffset', -200, 200);
    if (style.rotateDeg !== undefined) c.number(style.rotateDeg, q + '.rotateDeg', -360, 360);
    if (style.hidden !== undefined && typeof style.hidden !== 'boolean') c.error(q + '.hidden', 'Expected true or false.');
    if (style.ghost !== undefined && c.array(style.ghost, q + '.ghost', 1, 8)) style.ghost.forEach((kind, k) => c.string(kind, `${q}.ghost[${k}]`, 128, ID));
  });
  if (t.water !== undefined && c.object(t.water, p + '.water', ['match', 'color'], ['animate'])) {
    checkMatch(c, t.water.match, p + '.water.match'); color(c, t.water.color, p + '.water.color');
    if (t.water.animate !== undefined && typeof t.water.animate !== 'boolean') c.error(p + '.water.animate', 'Expected true or false.');
  }
  if (t.numberTokens !== undefined && c.object(t.numberTokens, p + '.numberTokens', ['field'], ['redField'])) {
    c.string(t.numberTokens.field, p + '.numberTokens.field', 128, ID);
    if (t.numberTokens.redField !== undefined) c.string(t.numberTokens.redField, p + '.numberTokens.redField', 128, ID);
  }
  if (t.layers !== undefined) c.oneOf(t.layers, p + '.layers', THREE_LAYER_MODES);
  if (t.ambient !== undefined && c.object(t.ambient, p + '.ambient', [], ['sky', 'ground', 'table'])) for (const key of ['sky', 'ground', 'table'] as const) if (t.ambient[key] !== undefined) color(c, t.ambient[key], `${p}.ambient.${key}`);
}

export function validateBoards(value: unknown): Issue[] {
  const c = new Checker('boards.json');
  if (!c.object(value, '$', ['formatVersion', 'boards'])) return c.issues;
  const f = value as unknown as BoardVisuals;
  c.oneOf(f.formatVersion, '$.formatVersion', ['board-visuals-1']);
  const ids = new Set<string>();
  if (c.array(f.boards, '$.boards', 0, 8)) f.boards.forEach((b, i) => {
    const p = `$.boards[${i}]`;
    if (!c.object(b, p, ['id', 'board', 'width', 'height', 'layers'], ['paths', 'runtime', 'edgePieces', 'placements'])) return;
    c.string(b.id, p + '.id', 128, ID); c.string(b.board, p + '.board', 128, ID);
    if (ids.has(b.id)) c.error(p, 'Duplicate board visual ID.'); ids.add(b.id);
    c.number(b.width, p + '.width', 1, 10000); c.number(b.height, p + '.height', 1, 10000);
    if (b.edgePieces !== undefined && c.object(b.edgePieces, p + '.edgePieces', ['source', 'width', 'height', 'tint'])) {
      c.string(b.edgePieces.source, p + '.edgePieces.source', 160, /^asset:[A-Za-z0-9][A-Za-z0-9:_.\/-]{0,127}$/);
      c.number(b.edgePieces.width, p + '.edgePieces.width', 1, 200); c.number(b.edgePieces.height, p + '.edgePieces.height', 1, 200);
      c.oneOf(b.edgePieces.tint, p + '.edgePieces.tint', ['owner']);
    }
    const placed = new Set<string>(); let slotCount = 0;
    if (b.placements !== undefined) {
      if (!b.edgePieces) c.error(p + '.placements', 'Piece placements require edgePieces.');
      if (c.array(b.placements, p + '.placements', 0, 4096)) b.placements.forEach((entry, j) => {
        const q = `${p}.placements[${j}]`;
        if (!c.object(entry, q, ['edge', 'slots'])) return;
        c.string(entry.edge, q + '.edge', 128, ID); if (placed.has(entry.edge)) c.error(q, 'Duplicate edge piece placement.'); placed.add(entry.edge);
        if (c.array(entry.slots, q + '.slots', 1, 16)) entry.slots.forEach((slot, k) => {
          slotCount++; const s = `${q}.slots[${k}]`;
          if (!c.object(slot, s, ['x', 'y', 'angle'], ['width', 'height'])) return;
          c.number(slot.x, s + '.x', 0, b.width); c.number(slot.y, s + '.y', 0, b.height); c.number(slot.angle, s + '.angle', -180, 180);
          if (slot.width !== undefined) c.number(slot.width, s + '.width', 1, 200);
          if (slot.height !== undefined) c.number(slot.height, s + '.height', 1, 200);
        });
      });
      if (slotCount > 4096) c.error(p + '.placements', 'At most 4096 display pieces per board.');
    }
    if (b.runtime !== undefined && c.object(b.runtime, p + '.runtime', ['renderer', 'fallback'], ['padding', 'background', 'ownAspect', 'fillNarrow', 'presets', 'three'])) {
      const runtime = b.runtime; c.oneOf(runtime.renderer, p + '.runtime.renderer', ['pixi', 'three']); c.oneOf(runtime.fallback, p + '.runtime.fallback', ['svg']);
      if (runtime.renderer === 'three' && runtime.three === undefined) c.error(p + '.runtime.three', 'The three renderer requires a three block.');
      if (runtime.three !== undefined) checkThree(c, runtime.three, p + '.runtime.three');
      if (runtime.padding !== undefined) c.number(runtime.padding, p + '.runtime.padding', 8, 120);
      if (runtime.background !== undefined) c.string(runtime.background, p + '.runtime.background', 7, /^#[0-9a-fA-F]{6}$/);
      for (const flag of ['ownAspect', 'fillNarrow'] as const) if (runtime[flag] !== undefined && typeof runtime[flag] !== 'boolean') c.error(p + '.runtime.' + flag, 'Expected true or false.');
      if (runtime.presets !== undefined && c.array(runtime.presets, p + '.runtime.presets', 0, 32)) runtime.presets.forEach((preset, j) => {
        const q = `${p}.runtime.presets[${j}]`;
        if (!c.object(preset, q, ['match'], ['arrival', 'change', 'move'])) return;
        for (const key of ['arrival', 'change', 'move'] as const) if (preset[key] !== undefined) c.oneOf(preset[key], q + '.' + key, BOARD_EFFECTS);
        checkMatch(c, preset.match, q + '.match');
      });
    }
    const paths = new Set<string>();
    if (b.paths !== undefined && c.array(b.paths, p + '.paths', 0, 4096)) b.paths.forEach((path, j) => {
      const q = `${p}.paths[${j}]`;
      if (!c.object(path, q, ['edge', 'parts'])) return;
      c.string(path.edge, q + '.edge', 128, ID);
      if (paths.has(path.edge)) c.error(q, 'Duplicate edge path.'); paths.add(path.edge);
      if (c.array(path.parts, q + '.parts', 1, 4)) path.parts.forEach((part, k) => {
        if (c.array(part, `${q}.parts[${k}]`, 2, 16)) part.forEach((point, n) => {
          if (!Array.isArray(point) || point.length !== 2 || point.some(v => typeof v !== 'number' || !Number.isFinite(v)) || point[0] < 0 || point[0] > b.width || point[1] < 0 || point[1] > b.height)
            c.error(`${q}.parts[${k}][${n}]`, 'Point must be inside the board extent.');
        });
      });
    });
    const layers = new Set<string>();
    if (c.array(b.layers, p + '.layers', 0, 32)) b.layers.forEach((l, j) => {
      const q = `${p}.layers[${j}]`;
      if (!c.object(l, q, ['id', 'role', 'rect'], ['source', 'region', 'fill', 'opacity', 'fit'])) return;
      c.string(l.id, q + '.id', 128, ID); if (layers.has(l.id)) c.error(q, 'Duplicate layer ID.'); layers.add(l.id);
      c.oneOf(l.role, q + '.role', ['background', 'terrain', 'decoration', 'foreground']);
      if (!Array.isArray(l.rect) || l.rect.length !== 4 || l.rect.some(n => typeof n !== 'number' || !Number.isFinite(n)) ||
        l.rect[0] < 0 || l.rect[1] < 0 || l.rect[2] <= 0 || l.rect[3] <= 0 || l.rect[0] + l.rect[2] > b.width || l.rect[1] + l.rect[3] > b.height) c.error(q + '.rect', 'Rectangle must be inside the board extent.');
      if (l.source !== undefined) c.string(l.source, q + '.source', 200, /^(asset:[A-Za-z0-9][A-Za-z0-9:_.\/-]{0,127}|[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+)$/);
      if (l.region !== undefined) c.string(l.region, q + '.region', 128, ID);
      if (l.fill !== undefined) c.string(l.fill, q + '.fill', 9, /^#[a-fA-F0-9]{6}([a-fA-F0-9]{2})?$/);
      if (l.opacity !== undefined) c.number(l.opacity, q + '.opacity', 0, 1);
      if (l.fit !== undefined) c.oneOf(l.fit, q + '.fit', ['contain', 'cover', 'fill']);
    });
  });
  return c.issues;
}
/** The 3D runtime needs the three-board capability, and every named model must be declared in the manifest. */
export function validateBoardThreeReferences(visuals: BoardVisuals | undefined, capabilities: readonly string[], models?: ReadonlySet<string>, standees?: ReadonlySet<string>): Issue[] {
  const c = new Checker('boards.json');
  for (const b of visuals?.boards ?? []) {
    const three = b.runtime?.three; if (!three) continue;
    if (!capabilities.includes('three-board')) c.error(b.id, 'Board runtime three requires the three-board capability.');
    if (three.layers !== undefined && !capabilities.includes('three-helpers')) c.error(b.id, 'layers requires the three-helpers capability.');
    for (const style of three.pieces ?? []) {
      if (style.model !== undefined && !models?.has(style.model)) c.error(b.id, `Unknown model "${style.model}"; declare it in manifest.models.`);
      for (const block of ['standee', 'slab', 'primitive', 'die', 'quad'] as const) if (style[block] !== undefined && !capabilities.includes('three-helpers')) c.error(b.id, `${block} requires the three-helpers capability.`);
      if (style.standee !== undefined) {
        if (!capabilities.includes('hosted-art')) c.error(b.id, 'Standees require the hosted-art capability.');
        if (style.standee.id !== undefined && !standees?.has(style.standee.id)) c.error(b.id, `Unknown standee "${style.standee.id}"; declare it in presentation.config.json standees and run bake-standees.`);
      }
    }
  }
  return c.issues;
}
export function validateBoardReferences(file: BoardVisuals, boards: Board[]): Issue[] {
  const structural = validateBoards(file);
  if (structural.some(i => i.severity === 'error')) return structural;
  const c = new Checker('boards.json');
  for (const v of file.boards) {
    const b = boards.find(b => b.id === v.board);
    if (!b || b.width !== v.width || b.height !== v.height) { c.error(v.id, 'Board ID and coordinate extent must match Describe.'); continue; }
    if (v.edgePieces && b.edges.reduce((n, edge) => n + (edge.segments ?? 1), 0) > 4096) c.error(v.id, 'At most 4096 display pieces per board.');
    for (const placement of v.placements ?? []) {
      const edge = b.edges.find(e => e.id === placement.edge);
      if (!edge) c.error(placement.edge, 'Unknown piece placement edge.');
      else if (placement.slots.length !== (edge.segments ?? 1)) c.error(placement.edge, 'Piece slot count must equal the described edge segments.');
    }
    for (const l of v.layers) if (l.region && !b.regions.some(r => r.id === l.region)) c.error(l.id, 'Unknown board region.');
    for (const path of v.paths ?? []) {
      const e = b.edges.find(e => e.id === path.edge), a = b.nodes.find(n => n.id === e?.a), z = b.nodes.find(n => n.id === e?.b);
      if (!e || !a || !z) { c.error(path.edge, 'Unknown board edge.'); continue; }
      const start = path.parts[0]?.[0], end = path.parts.at(-1)?.at(-1);
      if (!start || !end || start[0] !== a.x || start[1] !== a.y || end[0] !== z.x || end[1] !== z.y)
        c.error(path.edge, 'Path endpoints must match the existing edge nodes in a-to-b order.');
    }
  }
  return c.issues;
}

/** Strict authoring checks; runtime keeps a schematic board if optional scenery is unavailable. */
export function validateBoardPresentation(scene: SceneFile, visuals: BoardVisuals | undefined, atlases: AtlasesFile | undefined, assets: Set<string> | undefined, capabilities: readonly string[], models?: ReadonlySet<string>, standees?: ReadonlySet<string>): Issue[] {
  const c = new Checker('boards.json'), frames = frameIndex(atlases ?? null);
  for (const b of visuals?.boards ?? []) if (b.runtime && b.runtime.renderer !== 'three' && !capabilities.includes('pixi-board')) c.error(b.id, 'Board runtime requires pixi-board capability.');
  c.issues.push(...validateBoardThreeReferences(visuals, capabilities, models, standees));
  if (assets) for (const b of visuals?.boards ?? []) for (const style of b.runtime?.three?.pieces ?? []) {
    const decal = style.primitive?.decal;
    if (decal?.startsWith('asset:') && !assets.has(decal.slice(6))) c.error(b.id, 'Unknown decal artwork asset.');
    const quad = style.quad?.asset;
    if (quad !== undefined && !assets.has(quad.slice(6))) c.error(b.id, 'Unknown quad artwork asset.');
  }
  for (const b of visuals?.boards ?? []) if (b.edgePieces) {
    if (!capabilities.includes('edge-pieces')) c.error(b.id, 'Edge pieces require edge-pieces capability.');
    if (assets && !assets.has(b.edgePieces.source.slice(6))) c.error(b.id, 'Unknown edge piece artwork asset.');
  }
  for (const b of visuals?.boards ?? []) for (const l of b.layers) {
    if (l.source?.startsWith('asset:')) { if (assets && !assets.has(l.source.slice(6))) c.error(l.id, 'Unknown board artwork asset.'); }
    else if (l.source && !frames.has(l.source)) c.error(l.id, 'Unknown board artwork frame.');
  }
  const visit = (inst: BrickInstance) => {
    if (inst.brick === 'complex-board') {
      if (!capabilities.includes('complex-board')) c.error('scene', 'complex-board requires its capability.');
      const id = inst.props?.visual;
      if (id !== undefined && !visuals?.boards.some(b => b.id === id && inst.select === `board:${b.board}`)) c.error('scene', 'Board visual must exist and match the selected board.');
    }
    if (inst.child) visit(inst.child);
    if (Array.isArray(inst.props?.slots)) for (const child of inst.props.slots as BrickInstance[]) visit(child);
  };
  for (const s of [scene.scene, ...(scene.narrow ? [scene.narrow] : []), ...(scene.solo ? [scene.solo] : [])]) {
    Object.values(s.place).flat().forEach(visit);
    for (const tab of s.views?.tabs ?? []) tab.bricks.forEach(visit);
  }
  return c.issues;
}
